import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { trackEvent } from '../../../hooks/useGoogleAnalytics';

const CreateRequestForm = ({ onSubmit, currentLanguage }) => {
  const [formData, setFormData] = useState({
    itemName: '',
    category: '',
    originCountry: '',
    destinationCountry: '',
    budget: '',
    deliveryDate: '',
    carrierFee: '',
    notes: '',
    prohibitedAcknowledged: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const translations = {
    en: {
      createRequest: 'Create Purchase Request',
      itemName: 'Item Name',
      itemNamePlaceholder: 'Enter item name',
      category: 'Category',
      selectCategory: 'Select category',
      originCountry: 'Origin Country',
      selectOrigin: 'Select origin country',
      destinationCountry: 'Destination Country',
      selectDestination: 'Select destination country',
      budget: 'Budget (KRW)',
      budgetPlaceholder: 'Enter budget amount',
      deliveryDate: 'Desired Delivery Date',
      carrierFee: 'Proposed Carrier Fee (KRW)',
      carrierFeePlaceholder: 'Enter carrier fee',
      notes: 'Additional Notes',
      notesPlaceholder: 'Enter any special instructions or requirements',
      prohibitedWarning: 'I acknowledge that I am not requesting prohibited items (weapons, drugs, hazardous materials, counterfeit goods)',
      submitRequest: 'Submit Request',
      requiredField: 'This field is required',
      mustAcknowledge: 'You must acknowledge the prohibited items policy'
    },
    ko: {
      createRequest: '구매 요청 생성',
      itemName: '상품명',
      itemNamePlaceholder: '상품명을 입력하세요',
      category: '카테고리',
      selectCategory: '카테고리 선택',
      originCountry: '출발 국가',
      selectOrigin: '출발 국가 선택',
      destinationCountry: '도착 국가',
      selectDestination: '도착 국가 선택',
      budget: '예산 (원)',
      budgetPlaceholder: '예산 금액 입력',
      deliveryDate: '희망 배송일',
      carrierFee: '제안 운반 수수료 (원)',
      carrierFeePlaceholder: '운반 수수료 입력',
      notes: '추가 메모',
      notesPlaceholder: '특별 지시사항이나 요구사항을 입력하세요',
      prohibitedWarning: '금지 품목(무기, 마약, 위험물, 위조품)을 요청하지 않음을 확인합니다',
      submitRequest: '요청 제출',
      requiredField: '필수 입력 항목입니다',
      mustAcknowledge: '금지 품목 정책을 확인해야 합니다'
    }
  };

  const t = translations?.[currentLanguage];

  const categoryOptions = [
    { value: 'electronics', label: currentLanguage === 'en' ? 'Electronics' : '전자제품' },
    { value: 'fashion', label: currentLanguage === 'en' ? 'Fashion & Accessories' : '패션 & 액세서리' },
    { value: 'cosmetics', label: currentLanguage === 'en' ? 'Cosmetics & Beauty' : '화장품 & 뷰티' },
    { value: 'food', label: currentLanguage === 'en' ? 'Food & Beverages' : '식품 & 음료' },
    { value: 'books', label: currentLanguage === 'en' ? 'Books & Media' : '도서 & 미디어' },
    { value: 'sports', label: currentLanguage === 'en' ? 'Sports & Outdoor' : '스포츠 & 아웃도어' },
    { value: 'toys', label: currentLanguage === 'en' ? 'Toys & Games' : '장난감 & 게임' },
    { value: 'health', label: currentLanguage === 'en' ? 'Health & Wellness' : '건강 & 웰니스' }
  ];

  const countryOptions = [
    { value: 'us', label: currentLanguage === 'en' ? 'United States' : '미국' },
    { value: 'jp', label: currentLanguage === 'en' ? 'Japan' : '일본' },
    { value: 'kr', label: currentLanguage === 'en' ? 'South Korea' : '대한민국' },
    { value: 'cn', label: currentLanguage === 'en' ? 'China' : '중국' },
    { value: 'uk', label: currentLanguage === 'en' ? 'United Kingdom' : '영국' },
    { value: 'fr', label: currentLanguage === 'en' ? 'France' : '프랑스' },
    { value: 'de', label: currentLanguage === 'en' ? 'Germany' : '독일' },
    { value: 'au', label: currentLanguage === 'en' ? 'Australia' : '호주' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const formatCurrency = (value) => {
    const number = value?.replace(/[^\d]/g, '');
    return number?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handleBudgetChange = (e) => {
    const formatted = formatCurrency(e?.target?.value);
    handleChange('budget', formatted);
  };

  const handleCarrierFeeChange = (e) => {
    const formatted = formatCurrency(e?.target?.value);
    handleChange('carrierFee', formatted);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.itemName?.trim()) newErrors.itemName = t?.requiredField;
    if (!formData?.category) newErrors.category = t?.requiredField;
    if (!formData?.originCountry) newErrors.originCountry = t?.requiredField;
    if (!formData?.destinationCountry) newErrors.destinationCountry = t?.requiredField;
    if (!formData?.budget) newErrors.budget = t?.requiredField;
    if (!formData?.deliveryDate) newErrors.deliveryDate = t?.requiredField;
    if (!formData?.carrierFee) newErrors.carrierFee = t?.requiredField;
    if (!formData?.prohibitedAcknowledged) newErrors.prohibitedAcknowledged = t?.mustAcknowledge;

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSubmit(formData);
      
      // Track request creation event
      trackEvent('request_creation', {
        item_category: formData?.category,
        from_country: formData?.originCountry,
        to_country: formData?.destinationCountry,
        max_budget: parseInt(formData?.budget?.replace(/,/g, '')),
        urgency: formData?.deliveryDate,
        page: '/buyer-dashboard'
      });
      
      setFormData({
        itemName: '',
        category: '',
        originCountry: '',
        destinationCountry: '',
        budget: '',
        deliveryDate: '',
        carrierFee: '',
        notes: '',
        prohibitedAcknowledged: false
      });
    } catch (error) {
      console.error('Request creation error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-md p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="ShoppingCart" size={24} color="var(--color-primary)" />
        </div>
        <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground">
          {t?.createRequest}
        </h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <Input
            label={t?.itemName}
            type="text"
            placeholder={t?.itemNamePlaceholder}
            value={formData?.itemName}
            onChange={(e) => handleChange('itemName', e?.target?.value)}
            error={errors?.itemName}
            required
          />

          <Select
            label={t?.category}
            placeholder={t?.selectCategory}
            options={categoryOptions}
            value={formData?.category}
            onChange={(value) => handleChange('category', value)}
            error={errors?.category}
            required
          />

          <Select
            label={t?.originCountry}
            placeholder={t?.selectOrigin}
            options={countryOptions}
            value={formData?.originCountry}
            onChange={(value) => handleChange('originCountry', value)}
            error={errors?.originCountry}
            required
            searchable
          />

          <Select
            label={t?.destinationCountry}
            placeholder={t?.selectDestination}
            options={countryOptions}
            value={formData?.destinationCountry}
            onChange={(value) => handleChange('destinationCountry', value)}
            error={errors?.destinationCountry}
            required
            searchable
          />

          <Input
            label={t?.budget}
            type="text"
            placeholder={t?.budgetPlaceholder}
            value={formData?.budget}
            onChange={handleBudgetChange}
            error={errors?.budget}
            required
          />

          <Input
            label={t?.deliveryDate}
            type="date"
            value={formData?.deliveryDate}
            onChange={(e) => handleChange('deliveryDate', e?.target?.value)}
            error={errors?.deliveryDate}
            required
          />

          <Input
            label={t?.carrierFee}
            type="text"
            placeholder={t?.carrierFeePlaceholder}
            value={formData?.carrierFee}
            onChange={handleCarrierFeeChange}
            error={errors?.carrierFee}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t?.notes}
          </label>
          <textarea
            placeholder={t?.notesPlaceholder}
            value={formData?.notes}
            onChange={(e) => handleChange('notes', e?.target?.value)}
            rows={4}
            className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-base resize-none"
          />
        </div>

        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <Checkbox
            label={t?.prohibitedWarning}
            checked={formData?.prohibitedAcknowledged}
            onChange={(e) => handleChange('prohibitedAcknowledged', e?.target?.checked)}
            error={errors?.prohibitedAcknowledged}
            required
          />
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          iconName="Send"
          iconPosition="right"
          fullWidth
        >
          {t?.submitRequest}
        </Button>
      </form>
    </div>
  );
};

export default CreateRequestForm;